'use client';

// Unified reusable side panel — one component, identical behaviour in the Pro
// Terminal (inline right rail) and on stock/ETF pages (fixed overlay). Built-in
// icon tabs: Watchlist · Scan · Volume · Alerts · News · Trade. Resizable in
// both via a left-edge drag handle (width is controlled by the parent so the
// terminal's chart and the page layout both reflow). The only terminal-specific
// extras are injected: `viewsBar` (saved workspaces) above the tabs and
// `extraTabs` (Settings) appended to the strip.

import React, { useRef, useState } from 'react';
import { ListTree, Activity, Radar, BarChart3, BellRing, Rss, Wallet, PanelRightClose, X, ExternalLink } from 'lucide-react';
import { cn } from '@/lib/utils';
import PanelWatchlist from './PanelWatchlist';
import PriceActionPanel from './PriceActionPanel';
import PanelNews from './PanelNews';
import { ScannerPanel } from '@/components/terminal/ScannerPanel';
import { VolumePanel } from '@/components/terminal/VolumePanel';
import { AlertsPanel } from '@/components/terminal/AlertsPanel';
import { TradeForm } from '@/components/portfolio/TradeForm';

export interface SidePanelExtraTab {
  key: string;
  label: string;
  icon: React.ReactNode;
  render: () => React.ReactNode;
}

interface SidePanelProps {
  symbol: string;
  lastPrice: number | null;
  /** Replay/historical as-of for the Trade tab — sent as the trade time and
   *  flags the price as a past (paused-replay / historical-day) price. Null =
   *  live (trade time = now). */
  asOfIso?: string | null;
  asOfLabel?: string | null;
  onSelectSymbol: (s: string) => void;
  /** Dismiss handler — the terminal collapses the rail; the overlay closes. */
  onDismiss?: () => void;
  dismissMode?: 'collapse' | 'close';
  /** Terminal-only saved-views bar (SavedFiltersBar), rendered above the tabs. */
  viewsBar?: React.ReactNode;
  /** Terminal-only extra tabs (e.g. Settings), appended after the base set. */
  extraTabs?: SidePanelExtraTab[];
  /** Controlled width (px) + setter — the parent owns persistence + reflow. */
  width: number;
  onWidthChange: (w: number) => void;
  minWidth?: number;
  maxWidth?: number;
  /** Show the "Open in Classic view" footer (terminal only). */
  showClassicLink?: boolean;
  /** Ticker-click behavior in the Watchlist/News tabs. 'select' (default,
   *  terminal) loads the symbol via onSelectSymbol; 'popup' (global) opens the
   *  app-standard TickerPopup mini-chart, matching the stock pages. */
  tickerMode?: 'select' | 'popup';
}

type TabKey = 'watchlist' | 'scan' | 'volume' | 'alerts' | 'news' | 'trade' | string;

const BASE_TABS: { key: TabKey; label: string; icon: React.ReactNode }[] = [
  { key: 'watchlist', label: 'Watchlist', icon: <ListTree size={15} /> },
  { key: 'priceaction', label: 'Live price action', icon: <Activity size={15} /> },
  { key: 'scan', label: 'Scan', icon: <Radar size={15} /> },
  { key: 'volume', label: 'Volume', icon: <BarChart3 size={15} /> },
  { key: 'alerts', label: 'Alerts', icon: <BellRing size={15} /> },
  { key: 'news', label: 'News Room', icon: <Rss size={15} /> },
  { key: 'trade', label: 'Trade', icon: <Wallet size={15} /> },
];

export default function SidePanel({
  symbol, lastPrice, asOfIso, asOfLabel, onSelectSymbol, onDismiss, dismissMode = 'collapse',
  viewsBar, extraTabs = [], width, onWidthChange, minWidth = 208, maxWidth = 700,
  showClassicLink = false, tickerMode = 'select',
}: SidePanelProps) {
  const [tab, setTab] = useState<TabKey>('watchlist');

  const widthRef = useRef(width);
  widthRef.current = width;
  const startResize = (e: React.MouseEvent) => {
    e.preventDefault();
    const startX = e.clientX;
    const startW = widthRef.current;
    const onMove = (ev: MouseEvent) => {
      // Right-anchored: dragging left widens the panel.
      const w = Math.max(minWidth, Math.min(maxWidth, startW + (startX - ev.clientX)));
      widthRef.current = w;
      onWidthChange(w);
    };
    const onUp = () => {
      document.removeEventListener('mousemove', onMove);
      document.removeEventListener('mouseup', onUp);
      document.body.style.userSelect = '';
    };
    document.body.style.userSelect = 'none';
    document.addEventListener('mousemove', onMove);
    document.addEventListener('mouseup', onUp);
  };

  const tabs = [...BASE_TABS, ...extraTabs.map((t) => ({ key: t.key, label: t.label, icon: t.icon }))];

  return (
    // Fills its parent; the parent owns the explicit width (terminal: a sized
    // flex column; global: the fixed overlay) so reflow stays unambiguous.
    <div className="relative flex h-full w-full flex-col border-l border-default bg-surface text-slate-200">
      {/* Drag-to-resize handle (desktop). */}
      <div onMouseDown={startResize} title="Drag to resize"
        className="absolute left-0 top-0 bottom-0 z-50 hidden w-1 -ml-0.5 cursor-ew-resize hover:bg-sky-500/40 md:block" />

      {viewsBar && (
        <div className="flex flex-shrink-0 items-center gap-2 border-b border-default px-2 py-1.5">
          <span className="text-[10px] font-semibold uppercase tracking-wide text-slate-500">Views</span>
          <div className="min-w-0 flex-1">{viewsBar}</div>
        </div>
      )}

      {/* Tab strip */}
      <div className="flex flex-shrink-0 items-stretch border-b border-default">
        {tabs.map((t) => (
          <button key={t.key} onClick={() => setTab(t.key)} title={t.label} aria-label={t.label}
            className={cn('flex flex-1 items-center justify-center py-2',
              tab === t.key ? 'border-b-2 border-sky-500 text-white' : 'text-slate-500 hover:text-slate-300')}>
            {t.icon}
          </button>
        ))}
        {onDismiss && (
          <button onClick={onDismiss} title={dismissMode === 'close' ? 'Close panel' : 'Collapse panel'}
            aria-label={dismissMode === 'close' ? 'Close panel' : 'Collapse panel'}
            className="flex items-center px-2 text-slate-500 hover:text-white">
            {dismissMode === 'close' ? <X size={16} /> : <PanelRightClose size={15} />}
          </button>
        )}
      </div>

      {/* Active tab */}
      <div className="min-h-0 flex-1 overflow-hidden">
        {tab === 'watchlist' && <PanelWatchlist activeSymbol={symbol} onSelectSymbol={onSelectSymbol} tickerMode={tickerMode} />}
        {tab === 'priceaction' && <PriceActionPanel symbol={symbol} />}
        {tab === 'scan' && <div className="h-full overflow-y-auto"><ScannerPanel activeSymbol={symbol} onSelectSymbol={onSelectSymbol} /></div>}
        {tab === 'volume' && <div className="h-full overflow-y-auto"><VolumePanel activeSymbol={symbol} onSelectSymbol={onSelectSymbol} /></div>}
        {tab === 'alerts' && <div className="h-full overflow-y-auto"><AlertsPanel symbol={symbol} lastPrice={lastPrice} /></div>}
        {tab === 'news' && <PanelNews symbol={symbol} onSelectSymbol={onSelectSymbol} tickerMode={tickerMode} />}
        {tab === 'trade' && (
          <div className="h-full overflow-y-auto p-3">
            <div className="rounded-lg border border-default bg-card-bg p-3">
              <div className="mb-2 text-xs font-semibold text-fg-muted">Trade {symbol || 'to portfolio'}</div>
              <TradeForm initialTicker={symbol} prefillPrice={lastPrice ?? undefined} asOfIso={asOfIso} asOfLabel={asOfLabel} />
            </div>
          </div>
        )}
        {extraTabs.map((t) => (tab === t.key ? <React.Fragment key={t.key}>{t.render()}</React.Fragment> : null))}
      </div>

      {showClassicLink && symbol && (
        <a href={`/stocks/${symbol}`} title="Open in classic Stocks view"
          className="flex flex-shrink-0 items-center justify-center gap-1.5 border-t border-default py-1.5 text-[11px] text-slate-400 hover:bg-white/5 hover:text-sky-400">
          <ExternalLink size={13} /> Open in Classic view
        </a>
      )}
    </div>
  );
}
